import {
  DialogFragment,
  PageFragment,
  PageFragmentProps,
  PageFragmentState,
  PageFragmentStyleFlags
} from "@/shared/PageFragment";
import React, {ReactElement} from "react";
import {
  StyledBoxColumn,
  StyledBoxColumnCard,
  StyledHeaderContainer,
  StyledSplitPaneRow,
} from "@/shared/StyledComponents";
import {CircularProgress, FormControlLabel, Switch, Typography} from "@mui/material";
import {
  BORDER_RADIUS,
  DIVIDER,
  PAGE_FRAGMENT_HALF_WIDTH,
  PD_LG,
  PD_MD,
  PD_SM,
  PD_XLG,
  PD_XLG2,
  PD_XSM
} from "@/shared/dimens";
import {colorLightPastelBlue, lightGray} from "@/shared/colors";
import {Page, Pages, PracticeData} from "@/libwyze-shared/types";
import {FormGenContainer} from "@/shared/FormGenContainer";
import {KeyTextStrings} from "@/shared/types";
import {BaseApp} from "@/shared/BaseApp";
import {MessageCampaignCoverImage} from "./MessageCampaignCoverImage";
import {BaseMessageCampaignCustomizeOptions, MESSAGE_CAMPAIGN_CHANNEL_KTM} from "@/vantage-shared/types";
import {MailTemplateId, MessagingSendChannel, MessagingTemplateId} from "@/libwyze-common/api/types";

function saveOptions(options: BaseMessageCampaignCustomizeOptions): void {
  BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
    await options.save();
  });
}

class CustomizeOptionsFragment extends DialogFragment<{
  options: BaseMessageCampaignCustomizeOptions,
  onOptionsChanged?: () => void,
} & PageFragmentProps> {

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return "Save";
  }

  onActionButtonClicked() {
    saveOptions(this.props.options);
  }

  renderContent(): React.ReactElement {
    return <FormGenContainer
      onContainerWillSave={() => {
        this.props.onOptionsChanged?.();
      }}
      saveActionButtonProvider={this}
      content={this.props.options}/>;
  }
}

enum MessageGenerateStatus {
  NONE,
  GENERATING,
  GENERATED,
}

export type BaseMessageCampaignFragmentState<MESSAGE, OPTIONS extends BaseMessageCampaignCustomizeOptions> =
  PageFragmentState & {
  message?: MESSAGE,
  messageGenerateStatus: MessageGenerateStatus,
  customizeOptions: OPTIONS,
}

export enum MessageCampaignConfigType {
  EMAIL,
  MESSAGING,
}

export type MessageCampaignConfig = {
  name: string,
  type: MessageCampaignConfigType,
  email?: {
    title: string,
    text: string,
    subject: string,
    replaceStrings: KeyTextStrings,
    templateId: MailTemplateId,
  },
  messaging?: {
    channel: MessagingSendChannel,
    title: string,
    text: string,
    templateId: MessagingTemplateId,
    templateVariables: string[],
  },
}

export abstract class BaseMessageCampaignFragment<MESSAGE, OPTIONS extends BaseMessageCampaignCustomizeOptions> extends PageFragment<PageFragmentProps, BaseMessageCampaignFragmentState<MESSAGE, OPTIONS>> {

  protected campaignConfig: MessageCampaignConfig;

  protected abstract generateMessage(practicePage: Page): Promise<MESSAGE>;

  protected abstract createCampaignConfig(): MessageCampaignConfig;

  protected abstract renderPreview(message: MESSAGE): ReactElement;

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    await super.fetchOnMount(forceReload);
    this.campaignConfig = this.createCampaignConfig();
  }

  protected onCreateState(props?: PageFragmentProps): BaseMessageCampaignFragmentState<MESSAGE, OPTIONS> {
    return {
      ...super.onCreateState(props),
      messageGenerateStatus: MessageGenerateStatus.NONE,
    };
  }

  componentDidUpdate(prevProps: Readonly<PageFragmentProps>, prevState: Readonly<BaseMessageCampaignFragmentState<MESSAGE, OPTIONS>>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.customizeOptions !== this.state.customizeOptions) {
      this.setState({
        messageGenerateStatus: MessageGenerateStatus.GENERATING,
      });
    }
    if (prevState.messageGenerateStatus !== MessageGenerateStatus.GENERATING && this.state.messageGenerateStatus === MessageGenerateStatus.GENERATING) {
      const fragment = this;
      (async function () {
        const practicePage = Pages.getInstance().getListItem(PracticeData.provider().practiceId);
        fragment.setState({
          message: await fragment.generateMessage(practicePage),
          messageGenerateStatus: MessageGenerateStatus.GENERATED,
        });
      })();
    }
  }

  renderContent(): ReactElement {
    const practicePage = Pages.getInstance().getListItem(PracticeData.provider().practiceId);
    const customizeOptions = this.state.customizeOptions;
    let data: { title?: string, text?: string, subject?: string };
    const config = this.campaignConfig;
    switch (config.type) {
      case MessageCampaignConfigType.EMAIL:
        data = {
          title: config.email.title,
          text: config.email.text,
          subject: `Email message`,
        };
        break;
      case MessageCampaignConfigType.MESSAGING:
        data = {
          title: config.messaging.title,
          text: config.messaging.text,
          subject: `${MESSAGE_CAMPAIGN_CHANNEL_KTM.findMetadata(config.messaging.channel).text} message`,
        };
        break;
    }
    if (!data) {
      return null;
    }
    return <StyledHeaderContainer
      title={data.title}
      text={data.text}
      buttonsElement={<StyledBoxColumn style={{gap: PD_XLG}}>
        <StyledBoxColumn
          style={{alignItems: "start", gap: PD_XSM, padding: PD_MD, backgroundColor: colorLightPastelBlue}}>
          <FormControlLabel
            control={<Switch defaultChecked={customizeOptions.enabled} onChange={(e, checked) => {
              customizeOptions.enabled = checked;
              saveOptions(customizeOptions);
            }}/>}
            label={`Enable ${config.name} campaign for ${practicePage.practicePageProfile.basicInfo.name}`}/>
          <Typography variant={"caption"}>
            When enabled, messages will be automatically sent to patients.
          </Typography>
        </StyledBoxColumn>
        {this.renderTestButtons()}
      </StyledBoxColumn>}
      headerStyle={{
        padding: PD_XLG2,
        margin: PD_XLG,
        borderRadius: BORDER_RADIUS,
        backgroundColor: lightGray,
        alignItems: "center",
        border: DIVIDER,
      }}
      imageElement={
        <div style={{
          position: "relative",
          width: "30%",
          minWidth: PAGE_FRAGMENT_HALF_WIDTH,
          flexShrink: 0,
        }}>
          <MessageCampaignCoverImage channel={customizeOptions.channel} name={customizeOptions.name}/>
          <Typography
            variant={"h6"}
            style={{
              position: "absolute",
              top: 28,
              right: 0,
              padding: `${PD_SM}px ${PD_LG}px`,
              background: this.theme.palette.secondary.main,
              color: this.theme.palette.primary.contrastText,
              borderRadius: `${PD_LG}px ${PD_LG}px 0 ${PD_LG}px`,
            }}>
            {data.subject}
          </Typography>
        </div>
      }
      // imageElement={
      //   <div style={{
      //     position: "relative",
      //     width: "40%",
      //     minWidth: PAGE_FRAGMENT_HALF_WIDTH,
      //     padding: PD_LG,
      //     flexShrink: 0,
      //   }}>
      //     <img src={this.campaignConfig.headerImage} style={{width: "100%", border: DIVIDER, verticalAlign: "top"}}/>
      //     <Typography style={{
      //       position: "absolute",
      //       top: 28,
      //       right: 0,
      //       padding: `${PD_SM}px ${PD_LG}px`,
      //       background: this.theme.palette.primary.main,
      //       color: this.theme.palette.primary.contrastText,
      //       borderRadius: `${PD_LG}px ${PD_LG}px 0 ${PD_LG}px`,
      //     }}>
      //       {this.campaignConfig.greeting}
      //     </Typography>
      //   </div>
      // }
    >
      <StyledBoxColumn style={{padding: PD_XLG, gap: PD_XLG}}>
        <StyledSplitPaneRow style={{gap: PD_LG}}>
          <StyledBoxColumnCard title={"Customize"}>
            <Typography variant="body2">
              Customize the message content that will be sent to your patients.
            </Typography>
            <CustomizeOptionsFragment
              options={this.state.customizeOptions}
              onOptionsChanged={() => this.setState({
                messageGenerateStatus: MessageGenerateStatus.GENERATING,
              })}/>
          </StyledBoxColumnCard>
          <StyledBoxColumnCard title={"Preview"}>
            <StyledBoxColumn style={{padding: PD_XLG, gap: PD_MD}}>
              {this.renderPreviewInternal()}
            </StyledBoxColumn>
          </StyledBoxColumnCard>
        </StyledSplitPaneRow>
      </StyledBoxColumn>
    </StyledHeaderContainer>;
  }

  protected renderTestButtons():
    ReactElement {
    return null;
  }

  private

  renderPreviewInternal()
    :
    ReactElement {
    switch (this.state.messageGenerateStatus) {
      case MessageGenerateStatus.NONE:
        return <Typography>No preview available.</Typography>;
      case MessageGenerateStatus.GENERATING:
        return <CircularProgress/>;
      case MessageGenerateStatus.GENERATED:
        return this.renderPreview(this.state.message);
    }
  }
}